import React, { useState } from 'react';

import { kebabCase } from 'lodash-es';

import { Button, Link, Text } from '@change/ds-core-ui';
import { Checkbox } from '@change/ds-forms';

import { Waypoint } from 'src/app/shared/components/scroll';
import { useTrackOnce } from 'src/app/shared/hooks/tracking';

import type { TaskShape } from '../../../../types';

import { activeTaskStyles } from './ActiveTask.styles';

type ActiveTaskProps = Omit<TaskShape, 'status'>;

export const ActiveTask: React.FC<ActiveTaskProps> = ({
	name,
	title,
	description,
	primaryActionText,
	primaryActionHref,
	primaryActionOnClick,
	onCheckboxChange,
	isFirstActive = false,
}) => {
	const trackOnce = useTrackOnce();
	const [isChecked, setIsChecked] = useState(false);

	const handleCheckboxChange = (newValue: boolean) => {
		if (newValue && onCheckboxChange) {
			setIsChecked(true);
			void onCheckboxChange();
		}
	};

	const styles = activeTaskStyles();

	const textContent = (
		<>
			<Text data-qa={`task-title-${kebabCase(name)}`} variant="body" className="text-size-body-bold" as="p">
				{title}
			</Text>
			<Text
				data-qa={`task-description-${kebabCase(name)}`}
				variant="body"
				size="small"
				className="text-typography-weak"
				as="p"
			>
				{description}
			</Text>
		</>
	);

	return (
		<div>
			<Waypoint onEnter={async () => trackOnce('petition_manage_tasks_view', { task: name, version: 'new_12_2025' })} />
			<div data-qa="petition-task" className="flex flex-col justify-between gap-4 py-5 md:flex-row md:gap-5">
				<div className={styles.checkboxWrapper()}>
					<Checkbox
						name={`task-checkbox-${kebabCase(name)}`}
						isReadOnly={!!isChecked}
						onChange={handleCheckboxChange}
						aria-label={title}
					/>
					{primaryActionHref ? (
						<Link
							href={primaryActionHref}
							onClick={primaryActionOnClick}
							data-qa={`task-text-link-${kebabCase(name)}`}
							className="flex-1 text-left"
						>
							{textContent}
						</Link>
					) : (
						<button
							type="button"
							onClick={primaryActionOnClick}
							data-qa={`task-text-button-${kebabCase(name)}`}
							className="flex-1 text-left"
						>
							{textContent}
						</button>
					)}
				</div>
				<div className="flex flex-col items-start gap-6 md:flex-row md:items-center">
					{primaryActionHref ? (
						<Button
							as="a"
							variant={isFirstActive ? 'primary' : 'secondary'}
							size="x-small"
							href={primaryActionHref}
							onClick={primaryActionOnClick}
							data-qa={`task-action-button-${kebabCase(name)}`}
							className="bg-fill-inverse-strong w-full md:w-40"
						>
							{primaryActionText}
						</Button>
					) : (
						<Button
							variant={isFirstActive ? 'primary' : 'secondary'}
							size="x-small"
							onClick={primaryActionOnClick}
							data-qa={`task-action-button-${kebabCase(name)}`}
							className="bg-fill-inverse-strong w-full md:w-40"
						>
							{primaryActionText}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};
